/*
  SaikoSpeed home page behavior (ratified direction A "Router").
  Every section behavior is a custom element: inline section scripts do not
  execute on editor/hot-reload injection, and connectedCallback can fire
  before children are parsed, so setup defers to DOMContentLoaded while the
  document is still loading.
*/
(function () {
  'use strict';

  function whenReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  function savedVehicle() {
    try {
      var match = document.cookie.match(/(?:^|; )hrllc_vehicle=([^;]+)/);
      if (!match) return null;
      var vehicle = JSON.parse(decodeURIComponent(match[1]));
      if (!vehicle || !vehicle.year || !vehicle.make || !vehicle.model) return null;
      return {
        year: String(vehicle.year),
        make: String(vehicle.make),
        model: String(vehicle.model),
        submodel: String(vehicle.submodel || vehicle.trim || '')
      };
    } catch (error) {
      return null;
    }
  }

  function vehicleLabel(vehicle) {
    return [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* ---------- hero: fitment instrument with a live cascade ---------- */

  /*
    The cascade is fed by a generated asset, not by scraping a search page.
    Search & Discovery caps a facet's value list at 100 and scopes it to the
    query's result set, so /search?q=a offered an arbitrary hundred of the
    catalog's vehicles and a different hundred on the next load. The asset
    carries every value the token sync derived (regenerate it with
    `node tools/fitment-code-sync/sync.cjs --emit-asset`).

    The search scrape stays as the fallback: if the asset is missing or
    unparseable the hero still cascades, and if both fail the bar still routes
    to the catalog.
  */
  var TOKEN_FALLBACK_URL = '/search?q=a';
  var TOKEN_CACHE_KEY = 'saiko:fitment-tokens';

  /*
    Fitment values arrive in two shapes and the hero must read both, exactly
    as the collection instrument does (hrllc-grid-v1.js buildTokenIndex):

      "2022|Honda|Civic Si"        per-year token from the sync
      "2022-2025 Honda Civic Si"   range label, the fallback the sync writes
                                   when a product blows the token list cap
      "2006 Mitsubishi Lancer"     single-year label

    Ranges expand to one row per year for the cascade, but every row keeps
    the ORIGINAL value as its source: that string is what Search & Discovery
    indexed, so it is the only thing the filter will match. A selection can
    be covered by several sources at once (a per-year token from one product,
    a range label from another), and Go applies them all OR'd. Matching only
    the pipe form would silently drop every range-only product.
  */
  function decodeTokenForParsing(token) {
    var t = String(token == null ? '' : token);
    if (t.indexOf('+') !== -1) t = t.replace(/\+/g, ' ');
    if (t.indexOf('%') !== -1) {
      try {
        t = decodeURIComponent(t);
      } catch (error) {
        /* a stray percent is not an encoding */
      }
    }
    return t.trim();
  }

  function fourDigitYear(raw) {
    var n = parseInt(raw, 10);
    if (!isFinite(n)) return null;
    if (String(raw).length === 4) return n;
    // Same pivot as the collection engine: two-digit years read as 2000s
    // until they run too far ahead of today, then as 1900s.
    var pivot = (new Date().getFullYear() % 100) + 2;
    return n <= pivot ? 2000 + n : 1900 + n;
  }

  function splitMakeModel(rest) {
    var parts = String(rest || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return null;
    return { make: parts[0], model: parts.slice(1).join(' ') };
  }

  function indexTokens(tokens) {
    var rows = [];
    (Array.isArray(tokens) ? tokens : []).forEach(function (raw) {
      var source = String(raw == null ? '' : raw).trim();
      if (!source) return;

      var decoded = decodeTokenForParsing(source);

      if (decoded.indexOf('|') !== -1) {
        var parts = decoded.split('|');
        if (parts[0] && parts[1] && parts[2]) {
          rows.push({ year: parts[0].trim(), make: parts[1].trim(), model: parts[2].trim(), source: source });
        }
        return;
      }

      var range = decoded.match(/^(\d{2,4})\s*-\s*(\d{2,4})\s+(.+)$/);
      if (range) {
        var start = fourDigitYear(range[1]);
        var end = fourDigitYear(range[2]);
        var spread = splitMakeModel(range[3]);
        if (start === null || end === null || !spread) return;
        var step = start <= end ? 1 : -1;
        for (var y = start; step > 0 ? y <= end : y >= end; y += step) {
          rows.push({ year: String(y), make: spread.make, model: spread.model, source: source });
        }
        return;
      }

      var single = decoded.match(/^(\d{2,4})\s+(.+)$/);
      if (single) {
        var year = fourDigitYear(single[1]);
        var one = splitMakeModel(single[2]);
        if (year === null || !one) return;
        rows.push({ year: String(year), make: one.make, model: one.model, source: source });
      }
    });
    return rows;
  }

  function readTokenCache(url) {
    try {
      var raw = window.sessionStorage.getItem(TOKEN_CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || cached.url !== url || !Array.isArray(cached.tokens)) return null;
      return { tokens: cached.tokens, param: cached.param || '' };
    } catch (error) {
      return null;
    }
  }

  function writeTokenCache(url, result) {
    try {
      window.sessionStorage.setItem(
        TOKEN_CACHE_KEY,
        JSON.stringify({ url: url, tokens: result.tokens, param: result.param })
      );
    } catch (error) {
      /* private mode, quota: the cascade works without the cache */
    }
  }

  function fetchTokenAsset(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('token asset ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        var tokens = payload && Array.isArray(payload.tokens) ? payload.tokens : [];
        if (!tokens.length) throw new Error('token asset empty');
        return { tokens: tokens, param: (payload && payload.param) || '' };
      });
  }

  function scrapeSearchTokens() {
    return fetch(TOKEN_FALLBACK_URL, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('token source ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var embed = parsed.querySelector('.hrllc-grid-v1-fitment-tokens');
        var holder = parsed.querySelector('[data-fitment-filter-param]');
        var tokens = embed ? JSON.parse(embed.textContent || '[]') : [];
        var param = holder ? holder.getAttribute('data-fitment-filter-param') : '';
        return { tokens: tokens, param: param };
      });
  }

  function fetchFitmentTokens(assetUrl) {
    var cacheKey = assetUrl || TOKEN_FALLBACK_URL;
    var cached = readTokenCache(cacheKey);
    if (cached) return Promise.resolve(cached);

    var load = assetUrl
      ? fetchTokenAsset(assetUrl).catch(scrapeSearchTokens)
      : scrapeSearchTokens();

    return load.then(function (result) {
      writeTokenCache(cacheKey, result);
      return result;
    });
  }

  /*
    The branded option panel, shared by the hero's Year/Make/Model segments
    and the wheels band's spec segments. Built from the live <select> at open
    time so it is never stale after a cascade rebuild, and scrolled to centre
    the current row: 34 model years and 62 offsets do not fit, and opening at
    the top puts the shopper's own value out of sight. scrollTop directly,
    never scrollIntoView, which would scroll the page too.
  */
  function paintOptionPanel(select, panel, onPick) {
    while (panel.firstChild) panel.removeChild(panel.firstChild);
    var current = null;
    Array.prototype.forEach.call(select.options, function (option) {
      if (option.hidden) return;
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'saiko-instrument__option';
      button.setAttribute('role', 'option');
      var selected = option.value === select.value;
      button.setAttribute('aria-selected', selected ? 'true' : 'false');
      button.textContent = option.textContent;
      if (selected && option.value) current = button;
      button.addEventListener('click', function (event) {
        event.stopPropagation();
        if (onPick) {
          onPick(option.value);
          return;
        }
        panel.parentNode.classList.remove('is-open');
        if (option.value === select.value) return;
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
      panel.appendChild(button);
    });
    panel.scrollTop = current
      ? Math.max(0, current.offsetTop - (panel.clientHeight - current.offsetHeight) / 2)
      : 0;
  }

  var SaikoHomeHero = /** @type {any} */ (function () {
    function noop() {}
    return noop;
  })();

  if (typeof HTMLElement === 'function') {
    SaikoHomeHero = class extends HTMLElement {
      connectedCallback() {
        whenReady(this.setup.bind(this));
      }

      setup() {
        if (this.dataset.saikoInit === 'true') return;
        this.dataset.saikoInit = 'true';
        this.rows = [];
        this.param = '';
        this.selection = { year: '', make: '', model: '' };

        var vehicle = savedVehicle();
        if (vehicle) {
          this.selection = { year: vehicle.year, make: vehicle.make, model: this.modelWithSubmodel(vehicle) };
          this.renderContinuity(vehicle);
        }
        this.paintFacades();

        var go = this.querySelector('[data-saiko-go]');
        if (go) go.addEventListener('click', this.onGo.bind(this));
        this.initPanels();

        var self = this;
        fetchFitmentTokens(this.dataset.tokenSource || '')
          .then(function (result) {
            self.rows = indexTokens(result.tokens);
            self.param = result.param || '';
            self.reconcileSelection();
            self.buildOptions();
          })
          .catch(function () {
            /* the bar still routes to the catalog without the cascade */
          });
      }

      modelWithSubmodel(vehicle) {
        // convermax rows bake the submodel into the model string ("Civic Si")
        var model = vehicle.model;
        if (vehicle.submodel && model.indexOf(vehicle.submodel) === -1) {
          model = (model + ' ' + vehicle.submodel).trim();
        }
        return model;
      }

      renderContinuity(vehicle) {
        var line = this.querySelector('[data-saiko-continuity]');
        var name = this.querySelector('[data-saiko-vehicle]');
        if (name) name.textContent = vehicleLabel(vehicle);
        if (line) line.hidden = false;
      }

      years() {
        var seen = {};
        this.rows.forEach(function (row) {
          seen[row.year] = true;
        });
        return Object.keys(seen).sort(function (a, b) { return Number(b) - Number(a); });
      }

      makes(year) {
        var seen = {};
        this.rows.forEach(function (row) {
          if (row.year === year) seen[row.make] = true;
        });
        return Object.keys(seen).sort();
      }

      models(year, make) {
        var seen = {};
        this.rows.forEach(function (row) {
          if (row.year === year && row.make === make) seen[row.model] = true;
        });
        return Object.keys(seen).sort();
      }

      matchingTokens() {
        // Every distinct source value covering the selection, deduped: one
        // vehicle is routinely carried by a per-year token on one product and
        // a range label on another, and Go has to apply both.
        var selection = this.selection;
        var values = [];
        this.rows.forEach(function (row) {
          if (row.year !== selection.year) return;
          if (row.make !== selection.make) return;
          if (row.model !== selection.model) return;
          if (values.indexOf(row.source) === -1) values.push(row.source);
        });
        return values;
      }

      reconcileSelection() {
        // a saved vehicle that no longer matches any token stays visible in the
        // facades but the cascade rebuilds honestly from the token data
        if (!this.selection.year) return;
        var year = this.selection.year;
        if (this.years().indexOf(year) === -1) return;
        if (this.selection.make && this.makes(year).indexOf(this.selection.make) === -1) {
          this.selection.make = '';
          this.selection.model = '';
        }
        if (this.selection.model && this.models(year, this.selection.make).indexOf(this.selection.model) === -1) {
          this.selection.model = '';
        }
      }

      fillSelect(select, values, placeholder, current) {
        if (!select) return;
        var html = '<option value="">' + placeholder + '</option>';
        values.forEach(function (value) {
          var escaped = value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
          html += '<option value="' + escaped + '">' + escaped + '</option>';
        });
        select.innerHTML = html;
        select.value = current || '';
        select.disabled = values.length === 0;
      }

      buildOptions() {
        var self = this;
        var yearSelect = this.querySelector('[data-saiko-seg="year"] select');
        var makeSelect = this.querySelector('[data-saiko-seg="make"] select');
        var modelSelect = this.querySelector('[data-saiko-seg="model"] select');
        this.fillSelect(yearSelect, this.years(), 'All years', this.selection.year);
        this.fillSelect(makeSelect, this.selection.year ? this.makes(this.selection.year) : [], 'All makes', this.selection.make);
        this.fillSelect(modelSelect, this.selection.make ? this.models(this.selection.year, this.selection.make) : [], 'All models', this.selection.model);

        if (yearSelect && !yearSelect.dataset.saikoBound) {
          yearSelect.dataset.saikoBound = 'true';
          yearSelect.addEventListener('change', function () {
            self.selection = { year: yearSelect.value, make: '', model: '' };
            self.buildOptions();
            self.paintFacades();
          });
        }
        if (makeSelect && !makeSelect.dataset.saikoBound) {
          makeSelect.dataset.saikoBound = 'true';
          makeSelect.addEventListener('change', function () {
            self.selection.make = makeSelect.value;
            self.selection.model = '';
            self.buildOptions();
            self.paintFacades();
          });
        }
        if (modelSelect && !modelSelect.dataset.saikoBound) {
          modelSelect.dataset.saikoBound = 'true';
          modelSelect.addEventListener('change', function () {
            self.selection.model = modelSelect.value;
            self.buildOptions();
            self.paintFacades();
          });
        }
      }

      initPanels() {
        // Fine pointers get the collection instrument's branded panel; touch
        // keeps the OS picker (faster, and the thumb is already there).
        if (!window.matchMedia || !window.matchMedia('(pointer: fine)').matches) return;
        var self = this;
        ['year', 'make', 'model'].forEach(function (key) {
          var seg = self.querySelector('[data-saiko-seg="' + key + '"]');
          var select = seg && seg.querySelector('select');
          if (!seg || !select) return;

          var panel = document.createElement('div');
          panel.className = 'saiko-instrument__panel';
          panel.setAttribute('role', 'listbox');
          var label = seg.querySelector('.lbl');
          if (label) panel.setAttribute('aria-label', label.textContent);
          seg.appendChild(panel);

          seg.addEventListener('click', function (event) {
            if (event.target.closest('.saiko-instrument__panel')) return;
            var wasOpen = seg.classList.contains('is-open');
            self.closePanels();
            if (wasOpen || select.disabled) return;
            // Show first, then build: a display:none panel measures zero, so
            // scrolling it to the current value would silently do nothing.
            // Both happen in one frame, so nothing paints in between.
            seg.classList.add('is-open');
            // Built at open time, so it is always the live option list even
            // after the cascade rebuilds the select underneath it.
            self.buildPanel(select, panel);
          });
        });

        document.addEventListener('click', function (event) {
          if (!event.target.closest('.saiko-instrument__seg')) self.closePanels();
        });
        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') self.closePanels();
        });
      }

      buildPanel(select, panel) {
        paintOptionPanel(select, panel);
      }

      closePanels() {
        Array.prototype.forEach.call(this.querySelectorAll('.saiko-instrument__seg.is-open'), function (seg) {
          seg.classList.remove('is-open');
        });
      }

      paintFacades() {
        var selection = this.selection;
        var placeholders = { year: 'All years', make: 'All makes', model: 'All models' };
        ['year', 'make', 'model'].forEach(function (key) {
          var seg = this.querySelector('[data-saiko-seg="' + key + '"]');
          if (!seg) return;
          var value = selection[key];
          var facade = seg.querySelector('[data-saiko-val]');
          if (facade) facade.textContent = value || placeholders[key];
          seg.classList.toggle('is-set', Boolean(value));
          var locked = (key === 'make' && !selection.year) || (key === 'model' && !selection.make);
          seg.classList.toggle('is-locked', locked);
        }, this);
      }

      onGo(event) {
        event.preventDefault();
        // /collections/all cannot facet fitment (the S&D value cap), so a full
        // selection routes to search where the fitment facet is live storewide
        var destination = '/collections/all';
        if (this.param && this.selection.year && this.selection.make && this.selection.model) {
          var params = this.matchingTokens().map(function (token) {
            return encodeURIComponent(this.param) + '=' + encodeURIComponent(token);
          }, this);
          if (params.length) {
            var query = encodeURIComponent(this.selection.make + ' ' + this.selection.model);
            destination = '/search?q=' + query + '&' + params.join('&');
          }
        }
        window.location.assign(destination);
      }
    };
    if (!customElements.get('saiko-home-hero')) {
      customElements.define('saiko-home-hero', SaikoHomeHero);
    }
  }

  /* ---------- wheels band: spec selector routed to a pattern collection ---------- */

  /*
    Options come from a generated asset for the same reason the hero's do:
    the home page has no collection context, so there are no collection.filters
    to read, and the storefront facet caps its value list at 100 and scopes it
    to the query's result set.

    The asset is scoped PER BOLT PATTERN, because bolt pattern is the routing
    key. Search & Discovery serves no filters at all above roughly 5,000
    published products, so Wheels & Tires (6,529) can never carry the spec
    facets and spec params are inert there. The six pattern collections are
    the only wheel surfaces that filter, and their spec inventories genuinely
    differ: 4x100 stops at 19in, 5x120 runs to 24in.
  */
  var WHEEL_CACHE_KEY = 'saiko:wheel-spec-tokens';

  function readWheelCache(url) {
    try {
      var raw = window.sessionStorage.getItem(WHEEL_CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (!cached || cached.url !== url || !Array.isArray(cached.patterns)) return null;
      return { params: cached.params || {}, patterns: cached.patterns };
    } catch (error) {
      return null;
    }
  }

  function fetchWheelSpecs(url) {
    if (!url) return Promise.reject(new Error('no wheel spec source'));
    var cached = readWheelCache(url);
    if (cached) return Promise.resolve(cached);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('wheel spec asset ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        var patterns = payload && Array.isArray(payload.patterns) ? payload.patterns : [];
        if (!patterns.length) throw new Error('wheel spec asset empty');
        var result = { params: (payload && payload.params) || {}, patterns: patterns };
        try {
          window.sessionStorage.setItem(
            WHEEL_CACHE_KEY,
            JSON.stringify({ url: url, params: result.params, patterns: result.patterns })
          );
        } catch (error) {
          /* private mode, quota: the bar works without the cache */
        }
        return result;
      });
  }

  /*
    The ratified vehicle-to-spec table, rendered by
    snippets/saikospeed-vehicle-specs.liquid. The rules here are the rules
    resolveVehicleSpec() enforces in hrllc-grid-v1.js: exact make, exact
    model, year inside the row's range, and no answer at all when no row
    covers the car. Bolt pattern is the spec where being wrong is a wheel
    coming off, so a near miss is worse than silence.
  */
  var vehicleSpecRowsCache = null;

  function vehicleSpecRows() {
    if (vehicleSpecRowsCache) return vehicleSpecRowsCache;
    var island = document.querySelector('[data-saikospeed-vehicle-specs]');
    var parsed = null;
    try {
      parsed = island ? JSON.parse(island.textContent || '') : null;
    } catch (error) {
      parsed = null;
    }
    vehicleSpecRowsCache = parsed && Array.isArray(parsed.rows) ? parsed.rows : [];
    return vehicleSpecRowsCache;
  }

  function resolveVehicleSpec(vehicle) {
    if (!vehicle) return null;
    var year = parseInt(vehicle.year, 10);
    var make = String(vehicle.make || '').trim();
    var model = String(vehicle.model || '').trim();
    if (vehicle.submodel && model.indexOf(vehicle.submodel) === -1) {
      model = (model + ' ' + vehicle.submodel).trim();
    }
    if (!year || !make || !model) return null;
    var rows = vehicleSpecRows();
    for (var i = 0; i < rows.length; i += 1) {
      var row = rows[i];
      if (row.mk === make && row.md === model && year >= row.a && year <= row.b) return row;
    }
    return null;
  }

  // "+35" / "0" / "-12": the stored values are normalized, but read them as
  // numbers anyway so the window compares and sorts correctly.
  function offsetNumber(value) {
    if (value === '' || value == null) return null;
    var n = parseFloat(String(value).replace(/^\+/, ''));
    return isFinite(n) ? n : null;
  }

  if (typeof HTMLElement === 'function' && !customElements.get('saiko-wheel-bar')) {
    customElements.define('saiko-wheel-bar', class extends HTMLElement {
      connectedCallback() {
        whenReady(this.setup.bind(this));
      }

      setup() {
        if (this.dataset.saikoInit === 'true') return;
        this.dataset.saikoInit = 'true';
        this.params = {};
        this.patterns = [];
        this.selection = { pattern: '', diameter: '', width: '', offsetMin: '', offsetMax: '' };
        this.vehicle = savedVehicle();
        this.vehicleSpec = resolveVehicleSpec(this.vehicle);

        var go = this.querySelector('[data-saiko-wheelgo]');
        if (go) go.addEventListener('click', this.onGo.bind(this));
        this.initPanels();
        this.paint();

        var self = this;
        fetchWheelSpecs(this.dataset.tokenSource || '')
          .then(function (payload) {
            self.params = payload.params;
            self.patterns = payload.patterns;
            self.prefillFromVehicle();
            self.buildOptions();
            self.paint();
          })
          .catch(function () {
            /* no options and Go stays held: the bar promises nothing it
               cannot deliver, and the Browse all wheels link still works */
          });
      }

      // The payoff the ratified table exists for: a Civic Si owner should not
      // have to know they are 5x114.3. Only patterns the store actually sells
      // as a collection can be pre-set, because only those can be routed to.
      prefillFromVehicle() {
        if (!this.vehicleSpec || !this.vehicleSpec.p) return;
        if (!this.entryFor(this.vehicleSpec.p)) return;
        this.selection.pattern = this.vehicleSpec.p;
      }

      entryFor(pattern) {
        for (var i = 0; i < this.patterns.length; i += 1) {
          if (this.patterns[i].p === pattern) return this.patterns[i];
        }
        return null;
      }

      currentEntry() {
        return this.selection.pattern ? this.entryFor(this.selection.pattern) : null;
      }

      seg(key) {
        return this.querySelector('[data-saiko-wheelseg="' + key + '"]');
      }

      fillSelect(select, values, placeholder, current) {
        if (!select) return;
        var html = '<option value="">' + placeholder + '</option>';
        values.forEach(function (value) {
          var escaped = String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
          html += '<option value="' + escaped + '">' + escaped + '</option>';
        });
        select.innerHTML = html;
        select.value = current || '';
        select.disabled = values.length === 0;
      }

      buildOptions() {
        var self = this;
        var entry = this.currentEntry();
        var patternSelect = this.seg('pattern') && this.seg('pattern').querySelector('select');
        var diameterSelect = this.seg('diameter') && this.seg('diameter').querySelector('select');
        var widthSelect = this.seg('width') && this.seg('width').querySelector('select');
        var minSelect = this.querySelector('[data-saiko-offset="min"]');
        var maxSelect = this.querySelector('[data-saiko-offset="max"]');

        this.fillSelect(
          patternSelect,
          this.patterns.map(function (row) { return row.p; }),
          'All patterns',
          this.selection.pattern
        );
        this.fillSelect(diameterSelect, entry ? entry.d : [], 'Any', this.selection.diameter);
        this.fillSelect(widthSelect, entry ? entry.w : [], 'Any', this.selection.width);
        this.fillSelect(minSelect, entry ? entry.o : [], 'Any', this.selection.offsetMin);
        this.fillSelect(maxSelect, entry ? entry.o : [], 'Any', this.selection.offsetMax);

        this.bind(patternSelect, function (value) {
          // A new pattern brings a different spec inventory, so any size that
          // does not exist behind it is dropped rather than carried into a
          // guaranteed empty grid.
          self.selection.pattern = value;
          var next = self.currentEntry();
          if (!next || next.d.indexOf(self.selection.diameter) === -1) self.selection.diameter = '';
          if (!next || next.w.indexOf(self.selection.width) === -1) self.selection.width = '';
          if (!next || next.o.indexOf(self.selection.offsetMin) === -1) self.selection.offsetMin = '';
          if (!next || next.o.indexOf(self.selection.offsetMax) === -1) self.selection.offsetMax = '';
        });
        this.bind(diameterSelect, function (value) { self.selection.diameter = value; });
        this.bind(widthSelect, function (value) { self.selection.width = value; });
        this.bind(minSelect, function (value) {
          self.selection.offsetMin = value;
          self.clampWindow('min');
        });
        this.bind(maxSelect, function (value) {
          self.selection.offsetMax = value;
          self.clampWindow('max');
        });
      }

      bind(select, apply) {
        if (!select || select.dataset.saikoBound === 'true') return;
        select.dataset.saikoBound = 'true';
        var self = this;
        select.addEventListener('change', function () {
          apply(select.value);
          self.buildOptions();
          self.paint();
        });
      }

      // Inverted window: the side the shopper did not just touch follows the
      // one they did. Same rule as the wheels grid.
      clampWindow(touched) {
        var min = offsetNumber(this.selection.offsetMin);
        var max = offsetNumber(this.selection.offsetMax);
        if (min === null || max === null || min <= max) return;
        if (touched === 'min') this.selection.offsetMax = this.selection.offsetMin;
        else this.selection.offsetMin = this.selection.offsetMax;
      }

      offsetLabel() {
        var min = this.selection.offsetMin;
        var max = this.selection.offsetMax;
        if (!min && !max) return 'Any';
        if (min && !max) return min + ' and up';
        if (!min && max) return 'Up to ' + max;
        if (min === max) return min;
        return min + ' to ' + max;
      }

      paint() {
        var selection = this.selection;
        var locked = !selection.pattern;
        var facades = {
          pattern: selection.pattern || 'All patterns',
          diameter: selection.diameter || 'Any',
          width: selection.width || 'Any',
          offset: this.offsetLabel()
        };
        var isSet = {
          pattern: Boolean(selection.pattern),
          diameter: Boolean(selection.diameter),
          width: Boolean(selection.width),
          offset: Boolean(selection.offsetMin || selection.offsetMax)
        };
        ['pattern', 'diameter', 'width', 'offset'].forEach(function (key) {
          var seg = this.seg(key);
          if (!seg) return;
          var facade = seg.querySelector('[data-saiko-val]');
          if (facade) facade.textContent = facades[key];
          seg.classList.toggle('is-set', isSet[key]);
          seg.classList.toggle('is-locked', key !== 'pattern' && locked);
        }, this);

        var go = this.querySelector('[data-saiko-wheelgo]');
        if (go) {
          go.classList.toggle('is-held', locked);
          go.setAttribute('aria-disabled', locked ? 'true' : 'false');
        }

        // The vehicle's own spec, stated the way the browse surfaces state
        // it: what the CAR takes, never a promise that anything on the other
        // end is verified for it. It stays put even if the shopper picks a
        // different pattern on purpose, because it is a fact about the car.
        var hint = this.querySelector('[data-saiko-wheelhint-text]');
        if (!hint) return;
        if (this.vehicleSpec && this.vehicleSpec.p) {
          hint.textContent = 'Your ' + vehicleLabel(this.vehicle) + ' takes ' + this.vehicleSpec.p + ' wheels.';
        } else if (locked) {
          hint.textContent = 'Pick a bolt pattern to shop.';
        } else {
          hint.textContent = '';
        }
      }

      initPanels() {
        var self = this;
        var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

        ['pattern', 'diameter', 'width', 'offset'].forEach(function (key) {
          var seg = self.seg(key);
          if (!seg) return;
          // The offset segment ALWAYS gets a panel: it carries two selects in
          // one cell, and two native pickers cannot share a tap target.
          var isRange = key === 'offset';
          if (!fine && !isRange) return;

          var panel = document.createElement('div');
          panel.className = 'saiko-instrument__panel' + (isRange ? ' saiko-instrument__panel--range' : '');
          panel.setAttribute('role', isRange ? 'group' : 'listbox');
          var label = seg.querySelector('.lbl');
          if (label) panel.setAttribute('aria-label', label.textContent);
          seg.appendChild(panel);

          seg.addEventListener('click', function (event) {
            if (event.target.closest('.saiko-instrument__panel')) return;
            var wasOpen = seg.classList.contains('is-open');
            self.closePanels();
            if (wasOpen || seg.classList.contains('is-locked')) return;
            // Show before building: a display:none panel measures zero, so
            // scrolling it to the current value would silently do nothing.
            seg.classList.add('is-open');
            if (isRange) self.buildRangePanel(panel);
            else paintOptionPanel(seg.querySelector('select'), panel);
          });
        });

        // Scoped to the whole bar, not to a segment. A held Go opens the
        // bolt-pattern panel for them, and that click keeps bubbling: a
        // segment-scoped guard closed the panel again in the same tick.
        document.addEventListener('click', function (event) {
          if (!event.target.closest('saiko-wheel-bar')) self.closePanels();
        });
        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') self.closePanels();
        });
      }

      // Two columns, because an offset is a window and not a value. Shopify
      // has no range param for a metafield facet, so Go expands the window
      // into every stocked offset inside it as repeated OR'd values, exactly
      // as the wheels grid does.
      buildRangePanel(panel) {
        while (panel.firstChild) panel.removeChild(panel.firstChild);
        var self = this;
        [
          { side: 'min', head: 'From' },
          { side: 'max', head: 'To' }
        ].forEach(function (column) {
          var select = self.querySelector('[data-saiko-offset="' + column.side + '"]');
          if (!select) return;
          var wrap = document.createElement('div');
          wrap.className = 'saiko-range__col';
          // A <div>, not a <p>: the band's own `.saiko-wheels__content p`
          // rule paints paragraphs white for the dark image behind them, and
          // it outranks a single class, so a <p> here is white on white.
          var head = document.createElement('div');
          head.className = 'saiko-range__head';
          head.textContent = column.head;
          wrap.appendChild(head);
          var list = document.createElement('div');
          list.className = 'saiko-range__list';
          wrap.appendChild(list);
          panel.appendChild(wrap);

          paintOptionPanel(select, list, function (value) {
            select.value = value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            self.buildRangePanel(panel);
          });
        });
      }

      closePanels() {
        Array.prototype.forEach.call(this.querySelectorAll('.saiko-wheels__seg.is-open'), function (seg) {
          seg.classList.remove('is-open');
        });
      }

      onGo(event) {
        event.preventDefault();
        var entry = this.currentEntry();
        if (!entry) {
          // Held, not dead: hand them the step that is missing.
          var seg = this.seg('pattern');
          if (seg && !seg.classList.contains('is-open')) seg.click();
          return;
        }

        var self = this;
        var query = [];
        var add = function (param, value) {
          if (!param || !value) return;
          query.push(encodeURIComponent(param) + '=' + encodeURIComponent(value));
        };
        // The bolt pattern is expressed by the DESTINATION, never as a param.
        // Applying the variant pattern facet on top would drop every wheel in
        // that collection whose variants carry no pattern value of their own.
        add(this.params.diameter, this.selection.diameter);
        add(this.params.width, this.selection.width);

        var min = offsetNumber(this.selection.offsetMin);
        var max = offsetNumber(this.selection.offsetMax);
        if ((min !== null || max !== null) && this.params.offset) {
          var inside = entry.o.filter(function (value) {
            var n = offsetNumber(value);
            if (n === null) return false;
            if (min !== null && n < min) return false;
            if (max !== null && n > max) return false;
            return true;
          });
          // An empty window shows zero honestly rather than silently dropping
          // the offset the shopper asked for. Same sentinel as the grid.
          if (!inside.length) inside = ['__none__'];
          inside.forEach(function (value) { add(self.params.offset, value); });
        }

        var root = String(this.dataset.collectionsRoot || '/collections').replace(/\/$/, '');
        window.location.assign(root + '/' + entry.route + (query.length ? '?' + query.join('&') : ''));
      }
    });
  }

  /* ---------- platform tabs ---------- */

  if (typeof HTMLElement === 'function' && !customElements.get('saiko-platform-tabs')) {
    customElements.define('saiko-platform-tabs', class extends HTMLElement {
      connectedCallback() {
        whenReady(this.setup.bind(this));
      }

      setup() {
        if (this.dataset.saikoInit === 'true') return;
        this.dataset.saikoInit = 'true';
        var root = this;
        var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-saiko-tabs] button'));
        tabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            tabs.forEach(function (other) { other.classList.toggle('is-active', other === tab); });
            root.querySelectorAll('[data-make-panel]').forEach(function (panel) {
              panel.hidden = panel.getAttribute('data-make-panel') !== tab.getAttribute('data-make');
            });
          });
        });
      }
    });
  }

  /* ---------- category popouts (touch toggle; hover is CSS) ---------- */

  if (typeof HTMLElement === 'function' && !customElements.get('saiko-category-grid')) {
    customElements.define('saiko-category-grid', class extends HTMLElement {
      connectedCallback() {
        whenReady(this.setup.bind(this));
      }

      setup() {
        if (this.dataset.saikoInit === 'true') return;
        this.dataset.saikoInit = 'true';
        var fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
        this.querySelectorAll('[data-saiko-cat]').forEach(function (wrap) {
          var label = wrap.querySelector('.saiko-ccard__label');
          if (!label) return;
          label.addEventListener('click', function (event) {
            if (fine) return;
            event.preventDefault();
            var open = wrap.classList.contains('is-open');
            document.querySelectorAll('[data-saiko-cat].is-open').forEach(function (other) {
              other.classList.remove('is-open');
            });
            if (!open) wrap.classList.add('is-open');
          });
        });
        if (!document.documentElement.dataset.saikoCatOutside) {
          document.documentElement.dataset.saikoCatOutside = 'true';
          document.addEventListener('click', function (event) {
            if (!event.target.closest('[data-saiko-cat]')) {
              document.querySelectorAll('[data-saiko-cat].is-open').forEach(function (wrap) {
                wrap.classList.remove('is-open');
              });
            }
          });
        }
      }
    });
  }

  /* ---------- reviews as incoming texts ---------- */

  if (typeof HTMLElement === 'function' && !customElements.get('saiko-text-reviews')) {
    customElements.define('saiko-text-reviews', class extends HTMLElement {
      connectedCallback() {
        whenReady(this.setup.bind(this));
      }

      disconnectedCallback() {
        if (this.timer) window.clearInterval(this.timer);
        if (this.arrival) window.clearTimeout(this.arrival);
      }

      reviews() {
        var embed = this.querySelector('[data-saiko-review-data]');
        try {
          var parsed = JSON.parse(embed ? embed.textContent : '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      starsHtml() {
        var star = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9z"/></svg>';
        return '<span class="saiko-txt__stars" aria-label="Rated 5 out of 5 stars">' + star + star + star + star + star + '</span>';
      }

      escapeHtml(value) {
        var div = document.createElement('div');
        div.textContent = String(value == null ? '' : value);
        return div.innerHTML;
      }

      messageHtml(review) {
        return '<div class="saiko-txt__msg">'
          + '<p class="saiko-txt__label"><strong>' + this.escapeHtml(review.author) + '</strong> &middot; ' + this.escapeHtml(review.meta) + '</p>'
          + '<div class="saiko-txt__bubble">' + this.starsHtml() + '<p>' + this.escapeHtml(review.body) + '</p></div>'
          + '</div>';
      }

      typingHtml(review) {
        return '<div class="saiko-txt__msg">'
          + '<p class="saiko-txt__label"><strong>' + this.escapeHtml(review.author) + '</strong> &middot; ' + this.escapeHtml(review.meta) + '</p>'
          + '<div class="saiko-txt__bubble"><span class="saiko-txt__typing"><span></span><span></span><span></span></span></div>'
          + '</div>';
      }

      setup() {
        if (this.dataset.saikoInit === 'true') return;
        this.dataset.saikoInit = 'true';
        var pool = this.reviews();
        var slots = Array.prototype.slice.call(this.querySelectorAll('[data-txt-slot]'));
        if (!pool.length || !slots.length) return;

        var self = this;
        this.next = 0;
        slots.forEach(function (slot) {
          slot.innerHTML = self.messageHtml(pool[self.next]);
          self.next = (self.next + 1) % pool.length;
        });

        var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced || pool.length <= slots.length) return;

        var paused = false;
        this.addEventListener('mouseenter', function () { paused = true; });
        this.addEventListener('mouseleave', function () { paused = false; });
        this.addEventListener('focusin', function () { paused = true; });
        this.addEventListener('focusout', function () { paused = false; });

        var slotIndex = 0;
        this.timer = window.setInterval(function () {
          if (paused || document.hidden) return;
          var slot = slots[slotIndex];
          slotIndex = (slotIndex + 1) % slots.length;
          var review = pool[self.next];
          self.next = (self.next + 1) % pool.length;
          slot.classList.remove('is-arriving');
          slot.innerHTML = self.typingHtml(review);
          self.arrival = window.setTimeout(function () {
            if (document.hidden) {
              slot.innerHTML = self.messageHtml(review);
              return;
            }
            slot.classList.add('is-arriving');
            slot.innerHTML = self.messageHtml(review);
          }, 1100);
        }, 4600);
      }
    });
  }

  /*
    The build-expert links on this page open Shopify Inbox through the shared
    activator walk in assets/saikospeed-inbox.js, which is loaded on every
    template and binds its own delegated [data-saiko-inbox] listener. This file
    carried a copy of that walk until the contact page needed one too; a third
    copy was the wrong answer and loading 47KB of home machinery for a click
    handler was worse.
  */

  // Guarded so the token index below can be required and exercised directly
  // by the contract tests; the browser path is unchanged.
  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined' && module.exports) module.exports = { indexTokens: indexTokens };
    return;
  }

  /*
    The wheels hub runs the same instrument primitives rather than a second
    copy of them: the branded option panel, the saved-vehicle read, the token
    index and the ratified vehicle-to-spec lookup. This file is loaded first
    on that template and deferred scripts execute in document order, so
    assets/saikospeed-wheels-hub.js can rely on the namespace being here.

    THE COOKIE WRITER IS DELIBERATELY NOT PART OF THIS. The home hero must
    never write hrllc_vehicle and a contract test enforces that against this
    file, so the hub owns its own writer.
  */
  window.SaikoUI = {
    whenReady: whenReady,
    savedVehicle: savedVehicle,
    vehicleLabel: vehicleLabel,
    paintOptionPanel: paintOptionPanel,
    indexTokens: indexTokens,
    resolveVehicleSpec: resolveVehicleSpec
  };

})();
